import { jsxs as _jsxs, jsx as _jsx } from "@emotion/react/jsx-runtime";
import { css } from '@emotion/react';
import { text, border, neutral, news } from '@guardian/src-foundations/palette';
import { headline, textSans } from '@guardian/src-foundations/typography';
import { space } from '@guardian/src-foundations';
import { until } from '@guardian/src-foundations/mq';
import { createAuthenticationEventParams } from '@root/src/lib/identity-component-event';
const containerStyles = css `
	padding-top: ${space[1]}px;
	padding-bottom: ${space[1]}px;
`;
const imageStyles = css `
	border-radius: 70px;
	width: 60px;
	height: 60px;
	${until.desktop} {
		width: 36px;
		height: 36px;
	}
`;
const imageWrapper = css `
	padding-bottom: ${space[1]}px;
	padding-right: ${space[2]}px;
`;
const headingStyles = css `
	${headline.xxsmall({ fontWeight: 'bold' })};
	padding-bottom: ${space[1]}px;
`;
const textStyles = css `
	${textSans.small()}
	${until.desktop} {
		${textSans.xxsmall()}
	}
	color: ${text.supporting};
	padding-bottom: ${space[1]}px;
`;
const headlineStyles = css `
	${headline.xxxsmall()}
	color: ${text.supporting};
	padding-bottom: ${space[1]}px;
`;
const usernameStyles = css `
	font-weight: 700;
	color: ${text.primary};
`;
const linkStyles = (palette) => css `
	color: ${palette.text.signInLink};
	text-decoration: none;
	border-bottom: 1px solid ${border.secondary};
	transition: border-color 0.15s ease-out;
	:hover {
		border-color: ${news[300]};
	}
`;
const rowUntilDesktop = css `
	display: flex;
	flex-direction: column;
	${until.desktop} {
		flex-direction: row;
	}
`;
const CommentCount = ({ count }) => {
    return (_jsxs("h2", Object.assign({ css: headingStyles }, { children: ["comments", ' ', _jsxs("span", Object.assign({ css: css `
					color: ${neutral[60]};
				` }, { children: ["(", count || '…', ")"] }), void 0)] }), void 0));
};
export const SignedInAs = ({ commentCount, palette, enableDiscussionSwitch, user, isClosedForComments, }) => {
    const isBanned = user &&
        user.privateFields &&
        user.privateFields.canPostComment === false;
    if (enableDiscussionSwitch === false) {
        // Discussion is disabled sitewide and user is signed in
        if (user) {
            return (_jsxs("div", Object.assign({ css: containerStyles }, { children: [_jsx(CommentCount, { count: commentCount }, void 0),
                    _jsx("span", Object.assign({ css: headlineStyles }, { children: "Commenting has been disabled at this time" }), void 0)] }), void 0));
        }
        // Discussion disabled sitewide and user logged out
        return (_jsxs("div", Object.assign({ css: containerStyles }, { children: [_jsx(CommentCount, { count: commentCount }, void 0),
                _jsxs("span", Object.assign({ css: headlineStyles }, { children: ["Commenting has been disabled at this time but you can still", ' ', _jsx("a", Object.assign({ href: `https://profile.theguardian.com/signin?INTCMP=DOTCOM_COMMENTS_SIGNIN&${createAuthenticationEventParams('signin_to_comment')}`, css: linkStyles(palette) }, { children: "sign in" }), void 0), ' ', "or", ' ', _jsx("a", Object.assign({ href: `https://profile.theguardian.com/register?INTCMP=DOTCOM_COMMENTS_REG&${createAuthenticationEventParams('register_to_comment')}`, css: linkStyles(palette) }, { children: "create your Guardian account" }), void 0), ' ', "to join the discussion when it's back"] }), void 0)] }), void 0));
    }
    if (isBanned) {
        // User is banned
        return (_jsxs("div", Object.assign({ css: containerStyles }, { children: [_jsx(CommentCount, { count: commentCount }, void 0),
                _jsxs("span", Object.assign({ css: headlineStyles }, { children: ["Commenting has been disabled for this account (", _jsx("a", Object.assign({ href: "https://www.theguardian.com/community-faqs#321a", css: linkStyles(palette) }, { children: "why?" }), void 0), ' ', ")"] }), void 0)] }), void 0));
    }
    if (user && isClosedForComments) {
        // The reader is logged in but the discussion is closed
        return (_jsxs("div", Object.assign({ css: containerStyles }, { children: [_jsx(CommentCount, { count: commentCount }, void 0),
                _jsx("span", Object.assign({ css: headlineStyles }, { children: "This discussion is closed for comments" }), void 0)] }), void 0));
    }
    if (!user && isClosedForComments) {
        // The discussion is closed and the reader is not logged in
        return (_jsxs("div", Object.assign({ css: containerStyles }, { children: [_jsx(CommentCount, { count: commentCount }, void 0),
                _jsxs("span", Object.assign({ css: headlineStyles }, { children: ["This discussion is now closed for comments but you can still", ' ', _jsx("a", Object.assign({ href: `https://profile.theguardian.com/signin?INTCMP=DOTCOM_COMMENTS_SIGNIN&${createAuthenticationEventParams('signin_to_comment')}`, css: linkStyles(palette) }, { children: "sign in" }), void 0), ' ', "or", ' ', _jsx("a", Object.assign({ href: `https://profile.theguardian.com/register?INTCMP=DOTCOM_COMMENTS_REG&${createAuthenticationEventParams('register_to_comment')}`, css: linkStyles(palette) }, { children: "create your Guardian account" }), void 0), ' ', "to join the discussion next time"] }), void 0)] }), void 0));
    }
    if (!user) {
        // The discussion is open but the reader is not logged in
        return (_jsxs("div", Object.assign({ css: containerStyles }, { children: [_jsx(CommentCount, { count: commentCount }, void 0),
                _jsxs("span", Object.assign({ css: headlineStyles }, { children: [_jsx("a", Object.assign({ href: `https://profile.theguardian.com/signin?INTCMP=DOTCOM_COMMENTS_SIGNIN&${createAuthenticationEventParams('signin_to_comment')}`, css: linkStyles(palette) }, { children: "Sign in" }), void 0), ' ', "or", ' ', _jsx("a", Object.assign({ href: `https://profile.theguardian.com/register?INTCMP=DOTCOM_COMMENTS_REG&${createAuthenticationEventParams('register_to_comment')}`, css: linkStyles(palette) }, { children: "create your Guardian account" }), void 0), ' ', "to join the discussion"] }), void 0)] }), void 0));
    }
    // Discussion open and user logged in
    return (_jsxs("div", Object.assign({ css: containerStyles }, { children: [_jsx(CommentCount, { count: commentCount }, void 0),
            _jsxs("div", Object.assign({ css: rowUntilDesktop }, { children: [_jsx("div", Object.assign({ css: imageWrapper }, { children: _jsx("img", { src: user.secureAvatarUrl ||
                                'https://avatar.guim.co.uk/no-user-image.gif', alt: user.displayName || 'Guardian User', css: imageStyles }, void 0) }), void 0),
                    _jsxs("div", Object.assign({ css: textStyles }, { children: ["Signed in as", _jsx("div", Object.assign({ css: usernameStyles }, { children: user.displayName || 'Guardian User' }), void 0)] }), void 0)] }), void 0)] }), void 0));
};
